/* DOM-level fallback translator for legacy English-source screens when the app language is NL.
   Replaces exact-match text nodes / placeholder / title / aria-label using NL from dict.js.
   Sidebar is excluded via [data-no-translate]. Nodes remember their original text so we can restore. */
import { NL } from "./dict";

const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "CODE", "PRE"]);
let observer = null;
let active = false;
const touched = new Set();

function lookup(raw) {
  const m = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
  const core = m[2];
  if (!core) return null;
  let hit = NL[core];
  if (hit == null) {
    const tail = core.match(/^(.*?)([…:：]|\.\.\.| →|→)$/);
    if (tail && NL[tail[1]] != null) hit = NL[tail[1]] + tail[2];
  }
  if (hit == null) return null;
  return m[1] + hit + m[3];
}

function excluded(node, forAttr = false) {
  let el = node.nodeType === 3 ? node.parentElement : node;
  for (; el; el = el.parentElement) {
    if (el.hasAttribute?.("data-no-translate")) return true;
    if (!forAttr && SKIP_TAGS.has(el.tagName)) return true;
  }
  return false;
}

function translateText(node) {
  if (excluded(node)) return;
  if (node.__pmDone == null || node.nodeValue !== node.__pmDone) node.__pmOrig = node.nodeValue;
  const out = lookup(node.__pmOrig);
  if (out != null && out !== node.nodeValue) node.nodeValue = out;
  node.__pmDone = node.nodeValue;
  touched.add(node);
}

function translateAttrs(el) {
  if (excluded(el, true)) return;
  for (const a of ATTRS) {
    if (!el.hasAttribute(a)) continue;
    const key = `__pmOrig_${a}`;
    const cur = el.getAttribute(a);
    if (el[key] == null || cur !== el[`__pmDone_${a}`]) el[key] = cur;
    const out = lookup(el[key]);
    if (out != null && out !== cur) el.setAttribute(a, out);
    el[`__pmDone_${a}`] = el.getAttribute(a);
    touched.add(el);
  }
}

function walk(root) {
  if (!root) return;
  if (root.nodeType === 3) { translateText(root); return; }
  if (root.nodeType !== 1) return;
  translateAttrs(root);
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n;
  while ((n = w.nextNode())) { if (n.nodeType === 3) translateText(n); else translateAttrs(n); }
}

export function enableAutoTranslate() {
  if (active) return;
  active = true;
  walk(document.body);
  observer = new MutationObserver((muts) => {
    for (const m of muts) {
      if (m.type === "characterData") translateText(m.target);
      else if (m.type === "attributes") translateAttrs(m.target);
      else m.addedNodes.forEach(walk);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

export function disableAutoTranslate() {
  if (!active) return;
  active = false;
  observer?.disconnect(); observer = null;
  for (const n of touched) {
    if (n.nodeType === 3) { if (n.__pmOrig != null && n.isConnected) n.nodeValue = n.__pmOrig; delete n.__pmOrig; delete n.__pmDone; }
    else for (const a of ATTRS) { const k = `__pmOrig_${a}`; if (n[k] != null && n.isConnected) n.setAttribute(a, n[k]); delete n[k]; delete n[`__pmDone_${a}`]; }
  }
  touched.clear();
}
